import { FC } from 'react';
import { Markup } from 'interweave';
import Dialog, { CustomDialogHeaderOrFooter } from 'components/common/Dialog/Dialog';
import ELEMENT from 'common/element';

type ConfirmDialogProps = {
  title: string;
  text: string;
  cannotBeUndone?: boolean;
  show: boolean;
  close: () => void;
  onSubmit: () => void;
  createFooter?: CustomDialogHeaderOrFooter;
  cancelCallback?: () => void;
  textStyleClassName?: string;
};

/**
 * This component is a simple specialized dialog that asks the user to confirm an action, for example deleting an API key.
 *
 * @prop title                 Title text for the dialog
 * @prop text                  Confirmation question to ask the user
 * @prop show                  Flag indicating that the dialog is open
 * @prop close                 Calling this function should set the show flag to false
 * @prop onSubmit              Function to call when the user has clicked OK
 * @prop textStyleClassName    className to provide additional styling to the modal text
 * @prop testId                uniquely identifying string for testing
 */
const ConfirmDialog: FC<ConfirmDialogProps> = ({
  title,
  text,
  show,
  close,
  onSubmit,
  createFooter,
  cancelCallback,
  textStyleClassName = '',
  cannotBeUndone,
}: ConfirmDialogProps) => (
  <Dialog
    title={title}
    show={show}
    close={close}
    okCallback={onSubmit}
    onEnterCallback={onSubmit}
    okButtonText="Yes"
    cancelButtonText="No"
    footer={createFooter}
    cancelCallback={cancelCallback}
    testId={ELEMENT.confirmDialog}
  >
    <div className={textStyleClassName}>
      <Markup content={text} noWrap />
      {cannotBeUndone && (
        <div>
          <br />
          This cannot be undone.
        </div>
      )}
    </div>
  </Dialog>
);

export default ConfirmDialog;
